import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const PORTRAIT = "/img/jess-portrait.jpg";

const scrollToId = (e, id) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};

const SacredReset = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !email.trim()) {
      toast.error("Just your first name and email — that's all I need.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sacred-reset", { name: name.trim(), email: email.trim() });
      const watchPath = location.pathname.startsWith("/pause-the-spiral")
        ? "/pause-the-spiral/watch"
        : "/sacred-reset/watch";
      window.location.href = watchPath;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went sideways — try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-[#F6F1EA]" data-testid="sacred-reset-page">
      {/* HERO */}
      <section className="pt-28 pb-14 sm:pt-32 sm:pb-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
          <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-6 inline-flex items-center gap-2" data-testid="hero-eyebrow">
            <Sparkles size={12} /> The Quiet Pause
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-[64px] text-[#4F5D4F] leading-[1.05] tracking-tight" data-testid="hero-headline">
            <em className="italic text-[#B89B63]">I Don't Know</em>
            <br />What I Believe Anymore.
          </h1>
          <p className="mt-8 font-serif text-xl sm:text-2xl text-[#4F5D4F] leading-snug font-light max-w-2xl mx-auto">
            A guided reset for when questioning your faith has you questioning yourself.
          </p>
          <div className="mt-8 space-y-2 text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed max-w-xl mx-auto">
            <p>You've read the books.</p>
            <p>Listened to the podcasts.</p>
            <p>Googled the questions.</p>
            <p>Prayed about it.</p>
            <p>Thought about it.</p>
            <p className="font-serif italic text-[#4F5D4F]">And somehow, you feel even more confused.</p>
            <p className="pt-3">That's ok. You don't have to have the answers right now.</p>
            <p>You can discover how to quiet the noise &amp; sift through what's true.</p>
          </div>

          <div className="mt-12">
            <a
              href="#opt-in"
              onClick={(e) => scrollToId(e, "opt-in")}
              className="group inline-flex items-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] px-8 py-4 text-xs tracking-eyebrow uppercase transition-colors"
              data-testid="hero-cta"
            >
              I Need This Reset <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <p className="mt-4 text-xs tracking-eyebrow uppercase text-[#8C9A88]">Free guided video + companion workbook</p>
          </div>
        </div>
      </section>

      {/* HER EXPERIENCE */}
      <section className="py-14 sm:py-16">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-8" data-testid="experience-title">
            When every question leads to <em className="italic text-[#B89B63]">ten more questions</em>...
          </h2>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-6">
            Maybe your brain won't stop:
          </p>
          <ul className="font-serif italic text-lg sm:text-xl text-[#4F5D4F]/90 leading-snug space-y-1.5 pl-1 mb-8">
            <li>"What if I'm wrong?"</li>
            <li>"What if God is disappointed in me?"</li>
            <li>"What if everything I was taught is true?"</li>
            <li>"What if it isn't?"</li>
            <li>"Who can I trust?"</li>
            <li>"How do I know what's actually true?"</li>
            <li>"Can I even trust myself?"</li>
          </ul>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-4">
            When you've spent years being taught what is true &amp; the "ONLY WAY," questioning
            can feel terrifying.
          </p>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-4">
            You don't have to solve your entire faith today.
          </p>
          <p className="font-serif italic text-xl text-[#4F5D4F]">
            You just need somewhere safe to pause, sift, and listen.
          </p>
        </div>
      </section>

      {/* WHAT THIS DOES */}
      <section className="py-14 sm:py-16 bg-[#D7B2A3]/15">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-6" data-testid="what-does-title">
            This isn't another resource telling you <em className="italic text-[#B89B63]">what to believe.</em>
          </h2>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-6">
            It's ten minutes to get quiet, breathe, and get curious about what's actually
            happening inside you.
          </p>
          <p className="text-base sm:text-lg text-[#4F5D4F]/85 font-light leading-relaxed mb-8">
            Through a guided video and companion workbook, I'll walk you through a simple
            5-step practice:
          </p>
          <div className="space-y-6" data-testid="five-steps">
            {[
              { label: "Pause", copy: "Name what's happening without trying to solve it." },
              { label: "Name", copy: "Notice which voices are competing for your attention — yours, preachers, Spirit, or someone else." },
              { label: "Sort", copy: "Separate what you were taught, what you're afraid of, and what you actually think now." },
              { label: "Hear", copy: "Reconnect with one thing you know, need, value, believe, or are allowed to question." },
              { label: "Choose", copy: "Identify one small, honest next step for the next 24 hours." },
            ].map((s, i) => (
              <div key={s.label} className="grid grid-cols-12 gap-4 items-baseline">
                <p className="col-span-4 sm:col-span-3 font-serif text-2xl sm:text-3xl text-[#B89B63] tracking-tight uppercase">{s.label}</p>
                <p className="col-span-8 sm:col-span-9 text-base sm:text-lg text-[#4F5D4F]/90 font-light leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 border-l-2 border-[#B89B63] pl-5">
            <p className="font-serif italic text-xl sm:text-2xl text-[#4F5D4F] leading-snug">
              You don't need to know everything to know something.
            </p>
          </div>
        </div>
      </section>

      {/* TRANSFORMATION */}
      <section className="py-14 sm:py-16">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-8" data-testid="transformation-title">
            You don't need more pressure. You need <em className="italic text-[#B89B63]">a little room to breathe.</em>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
            <div className="bg-[#D7B2A3]/20 border border-[#D7B2A3]/60 p-6" data-testid="transformation-before">
              <p className="text-[10px] tracking-eyebrow uppercase text-[#B89B63] mb-3">Before</p>
              <ul className="font-serif italic text-lg text-[#4F5D4F]/90 space-y-1 leading-snug">
                <li>Overwhelmed.</li>
                <li>Afraid she's getting it wrong.</li>
                <li>Consumed by research + other people's opinions.</li>
                <li>Unsure which voice to trust.</li>
                <li>Disconnected from herself.</li>
                <li>Pressured to figure everything out.</li>
              </ul>
            </div>
            <div className="bg-[#4F5D4F]/5 border border-[#4F5D4F]/30 p-6" data-testid="transformation-after">
              <p className="text-[10px] tracking-eyebrow uppercase text-[#B89B63] mb-3">After</p>
              <ul className="font-serif italic text-lg text-[#4F5D4F] space-y-1 leading-snug">
                <li>"I don't have to figure everything out today."</li>
                <li>"I can ask questions without immediately answering them."</li>
                <li>"I can notice which voices are influencing me."</li>
                <li>"I can hear myself a little more clearly."</li>
                <li>"I know what I need right now."</li>
                <li>"I have one next step."</li>
              </ul>
            </div>
          </div>

          <div className="border-l-2 border-[#B89B63] pl-5 space-y-4">
            <div>
              <p className="text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-1">From</p>
              <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F]/85 leading-snug">
                "I don't know what I believe anymore &amp; I don't know how to figure out what's true."
              </p>
            </div>
            <div>
              <p className="text-[10px] tracking-eyebrow uppercase text-[#B89B63] mb-1">To</p>
              <p className="font-serif italic text-lg sm:text-xl text-[#4F5D4F] leading-snug">
                "I can breathe. I can question. I know how to quiet the noise &amp; discern truth."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT SHE GETS */}
      <section className="py-14 sm:py-16 bg-[#D7B2A3]/15">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-10" data-testid="gets-title">
            Here's what's <em className="italic text-[#B89B63]">waiting for you.</em>
          </h2>
          <div className="space-y-8">
            <div>
              <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-2">01</p>
              <h3 className="font-serif text-2xl text-[#4F5D4F] mb-2">A 10-minute guided video experience with Jess</h3>
              <p className="text-base text-[#4F5D4F]/85 font-light leading-relaxed">
                This should feel like a mini coaching experience—not a lecture or theological lesson.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-2">02</p>
              <h3 className="font-serif text-2xl text-[#4F5D4F] mb-2">A beautiful companion workbook</h3>
              <p className="text-base text-[#4F5D4F]/85 font-light leading-relaxed">
                Use it to identify what is overwhelming you, name the voices competing for your attention, sort through inherited messages, reconnect with your own thoughts and needs, and choose one honest next step.
              </p>
            </div>
            <div>
              <p className="text-xs tracking-eyebrow uppercase text-[#B89B63] mb-2">03</p>
              <h3 className="font-serif text-2xl text-[#4F5D4F] mb-2">A practice you can come back to</h3>
              <p className="text-base text-[#4F5D4F]/85 font-light leading-relaxed">
                This is not something you use once and forget. Return to the PAUSE practice whenever faith questions, fear, outside opinions, or overthinking start getting loud again.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT JESS */}
      <section className="py-14 sm:py-16">
        <div className="max-w-3xl mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-5">
            <div className="relative aspect-[3/4] max-w-[260px] mx-auto md:mx-0">
              <div className="absolute -top-3 -left-3 w-16 h-16 bg-[#D7B2A3]/60 z-0" />
              <img src={PORTRAIT} alt="Jess Eisaman" className="relative z-10 w-full h-full object-cover" data-testid="about-jess-photo" />
            </div>
          </div>
          <div className="md:col-span-7">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#4F5D4F] leading-tight tracking-tight mb-6" data-testid="about-jess-title">
              Hi, I'm <em className="italic text-[#B89B63]">Jess</em>.
            </h2>
            <div className="space-y-4 text-base text-[#4F5D4F]/85 font-light leading-relaxed">
              <p>
                I'm a Faith &amp; Identity Coach, seminary graduate, former Christian counselor,
                and ICF-certified coach.
              </p>
              <p>
                But more importantly for this conversation—I know what it feels like to question
                the beliefs that once shaped your entire world and realize you're also questioning
                yourself.
              </p>
              <p>
                I know the exhausting search for the "right" answer &amp; the feeling like my
                whole world has crumbled beneath my feet.
              </p>
              <p>
                And I know what it's like to begin discovering that maybe the work isn't finding
                another person to tell you what to believe. Maybe it's learning how to hear and
                trust your own voice or the Spirit.
              </p>
              <p className="font-serif italic text-[#4F5D4F]">
                That's the kind of space I create here.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OPT-IN + FINAL CTA combined */}
      <section id="opt-in" className="py-14 sm:py-16 bg-[#4F5D4F] text-[#F6F1EA]">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight tracking-tight mb-6" data-testid="final-cta-title">
            You don't have to <em className="italic text-[#D7B2A3]">know yet.</em>
          </h2>
          <div className="space-y-2 text-[#F6F1EA]/90 font-light leading-relaxed max-w-lg mx-auto mb-10">
            <p>You don't have to choose a label.</p>
            <p>You don't have to make a major decision.</p>
            <p>You don't have to explain yourself to anyone.</p>
            <p className="font-serif italic text-[#D7B2A3] pt-3">
              For the next ten minutes, you can simply pause.
            </p>
          </div>

          <div className="max-w-md mx-auto bg-[#F6F1EA] text-[#4F5D4F] p-8 sm:p-10">
            <form onSubmit={submit} data-testid="optin-form">
              <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2 text-left">First name</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name is fine"
                className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 h-11 mb-5"
                data-testid="optin-name"
              />
              <label className="block text-[10px] tracking-eyebrow uppercase text-[#8C9A88] mb-2 text-left">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@somewhere.com"
                className="rounded-none border-[#D7B2A3] bg-[#F6F1EA]/40 h-11 mb-8"
                data-testid="optin-email"
              />
              <button
                type="submit"
                disabled={submitting}
                className="group inline-flex w-full items-center justify-center gap-3 bg-[#4F5D4F] text-[#F6F1EA] hover:bg-[#3a453a] disabled:opacity-40 px-7 py-4 text-xs tracking-eyebrow uppercase transition-colors"
                data-testid="optin-submit"
              >
                {submitting ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : <>Yes, I'm Ready to Pause <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" /></>}
              </button>
              <p className="text-xs text-[#8C9A88] italic text-center mt-5 leading-relaxed">
                Get the free 10-minute guided Faith &amp; Identity Reset + companion workbook.
                <br />I do not spam, and you can unsubscribe at any time.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10 border-t border-[#D7B2A3]/40" data-testid="sr-footer">
        <div className="max-w-2xl mx-auto px-6 text-center text-sm text-[#4F5D4F]/80 font-light">
          <p className="font-serif italic text-[#4F5D4F]">Jess Eisaman Co.</p>
          <p className="mt-2">Faith &amp; Identity Coaching for women questioning what they were taught to believe.</p>
          <div className="mt-4 flex items-center justify-center gap-5 text-xs tracking-eyebrow uppercase">
            <Link to="/" className="text-[#4F5D4F] hover:text-[#B89B63]">jesseisamanco.com</Link>
            <a href="https://www.instagram.com/jesseisaman/" target="_blank" rel="noreferrer" className="text-[#4F5D4F] hover:text-[#B89B63]">Instagram</a>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default SacredReset;
